import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, TextField, Typography, CircularProgress, Paper } from "@mui/material";
import Modal from "./Modal";
import { createValidator, compose, required, integer, maxLength, isDirty as dirty } from "../../utils/validationUtils";

const EditAnswerOptionModal = ({ open = false, onClose, initialData, onSubmit, loading = false }) => {
  const [form, setForm] = useState({ option: "", label: "", position: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      option: initialData?.option ?? "",
      label: initialData?.label ?? "",
      position: initialData?.position === 0 || initialData?.position ? String(initialData.position) : "",
    });
    setErrors({});
    setTouched({});
  }, [open, initialData]);

  const validator = useMemo(
    () =>
      createValidator({
        option: compose(required("La opción es obligatoria."), maxLength(100)),
        label: compose(required("La etiqueta es obligatoria."), maxLength(200)),
        position: compose(required("La posición es obligatoria."), integer({ min: 0, msgInvalid: "Debe ser un entero ≥ 0." })),
      }),
    [],
  );

  const validate = (state = form) => validator(state);

  const isDirty = useMemo(() => {
    const p = initialData?.position;
    const initialPosition = p === 0 || p ? String(p) : "";
    const init = {
      option: initialData?.option ?? "",
      label: initialData?.label ?? "",
      position: initialPosition,
    };
    return dirty(init, form);
  }, [form, initialData]);

  const at = initialData?.answerType || {};

  const handleChange = (field) => (ev) => {
    const value = ev.target.value;
    setForm((s) => ({ ...s, [field]: value }));
    if (touched[field]) setErrors(validate({ ...form, [field]: value }));
  };

  const handleBlur = (field) => () => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors(validate());
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault();
    const v = validate();
    setErrors(v);
    setTouched({ option: true, label: true, position: true });
    if (Object.keys(v).length) return;

    setSubmitting(true);
    const ok = await onSubmit?.({
      id: initialData?.id,
      option: form.option.trim(),
      label: form.label.trim(),
      position: Number(form.position),
      answer_type_id: at.id,
    })
      .then(() => true)
      .catch(() => false);

    setSubmitting(false);
    if (ok) onClose?.();
  };

  const isSaving = submitting || loading;

  return (
    <Modal open={open} handleClose={onClose} width="580px">
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap={3}>
            {/* Encabezado */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                Actualizar opción de respuesta
              </Typography>

              {/* Meta */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(0,0,0,0.7)",
                    backgroundColor: "rgba(0,0,0,0.08)",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: 500,
                  }}
                >
                  {`ID externo del tipo: ${at?.external_id || "—"}`}
                </Typography>
                {initialData?.id && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      backgroundColor: "rgba(0,0,0,0.08)",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {`ID opción: ${initialData.id}`}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Contenido */}
            <Box sx={{ p: 3, pt: 0 }}>
              <Stack spacing={3}>
                <TextField
                  label="Opción"
                  value={form.option}
                  onChange={handleChange("option")}
                  onBlur={handleBlur("option")}
                  error={!!errors.option}
                  helperText={errors.option || "Código interno de la opción."}
                  inputProps={{ maxLength: 100 }}
                  autoFocus
                  required
                  fullWidth
                />

                <TextField
                  label="Etiqueta"
                  value={form.label}
                  onChange={handleChange("label")}
                  onBlur={handleBlur("label")}
                  error={!!errors.label}
                  helperText={errors.label || "Texto visible para el usuario."}
                  inputProps={{ maxLength: 200 }}
                  required
                  fullWidth
                />

                <TextField
                  label="Posición"
                  value={form.position}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, "");
                    handleChange("position")({ target: { value: v } });
                  }}
                  onBlur={handleBlur("position")}
                  error={!!errors.position}
                  helperText={errors.position || "Entero igual o mayor a 0."}
                  inputProps={{ inputMode: "numeric" }}
                  required
                  fullWidth
                />
              </Stack>

              {/* Acciones */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 3, mt: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={isSaving} sx={{ borderRadius: 1, px: 3 }}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSaving || !isDirty || Object.keys(validate()).length > 0}
                  sx={{ minWidth: 120, borderRadius: 1, px: 3 }}
                >
                  {isSaving ? (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <span>Actualizando…</span>
                    </Stack>
                  ) : (
                    "Actualizar"
                  )}
                </Button>
              </Box>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Modal>
  );
};

export default EditAnswerOptionModal;
